
import React from 'react';
import {
    ImageOverlay,
    ImageOverlayItemProps,
    OverlayItemType,
} from 'ui/components/3-organisms/ImageOverlay';

interface LinkValue {
    name?: string;
    url: string;
    target?: string;
    queryString?: string;
}

interface OverlayItemProperties {
    position: string;
    type: OverlayItemType;
    text?: string;
    link?: LinkValue | LinkValue[];
    textColor?: string;
    backgroundColor?: string;
    backgroundOpacity?: number | string;
    buttonFontSize?: number | string;
    buttonHeadingLevel?: string;
}

interface OverlayItemBlock {
    content: {
        properties: OverlayItemProperties;
    };
}

interface ImageProperties {
    url: string;
    properties?: {
        altText?: string;
    };
}

interface ImageOverlayProperties {
    image?: ImageProperties;
    height?: string | number;
    overlayColor?: string;
    overlayOpacity?: number;
    overlayItems?: OverlayItemBlock[];
}

interface ImageOverlayFeatureProps {
    content: {
        properties: ImageOverlayProperties;
    };
}

const toHex = (value?: string): string | undefined => {
    if (!value) return undefined;

    const normalized = value.trim();

    if (!/^#?[0-9a-fA-F]{6}$/.test(normalized)) {
        return undefined;
    }

    return normalized.startsWith('#')
        ? normalized
        : `#${normalized}`;
};

const normalizeHeight = (
    value?: string | number
): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    const normalized = String(value).trim();

    if (!/^\d+(?:\.\d+)?(?:px)?$/i.test(normalized)) {
        return undefined;
    }

    const height = Number(normalized.replace(/px$/i, ''));

    return height > 0 ? `${height}px` : undefined;
};

const normalizeFontSize = (
    value?: string | number
): string | undefined => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    const fontSize = String(value).trim().toLowerCase();

    const allowedSizes = [
        '8pt', '10pt', '12pt', '14pt',
        '18pt', '24pt', '36pt'
    ];

    return allowedSizes.includes(fontSize)
        ? fontSize
        : undefined;
};

const normalizeOpacity = (
    value?: number | string
): number => {
    if (value === undefined || value === null || value === '') {
        return 100;
    }

    const opacity = Number(value);

    if (!Number.isFinite(opacity)) {
        return 100;
    }

    return Math.min(100, Math.max(0, opacity));
};

export const ImageOverlayFeature: React.FC<ImageOverlayFeatureProps> = ({
    content,
}) => {
    const properties = content?.properties;

    if (!properties?.image?.url) {
        return null;
    }

    const {
        image,
        height,
        overlayColor,
        overlayOpacity,
        overlayItems = [],
    } = properties;

    const items: ImageOverlayItemProps[] = overlayItems.map(
        (itemBlock, index) => {
            const item = itemBlock.content.properties;

            const [rowValue, colValue] = (item.position || '1,1')
                .split(',')
                .map((value) => parseInt(value, 10));

            const row = Math.min(
                4,
                Math.max(0, (rowValue || 1) - 1)
            );

            const col = Math.min(
                4,
                Math.max(0, (colValue || 1) - 1)
            );

            const linkData = Array.isArray(item.link)
                ? item.link[0]
                : item.link;

            const html =
                item.type === 'Knap'
                    ? linkData?.name || item.text || ''
                    : item.text || '';

            return {
                id: index,
                type: item.type,
                html,
                linkUrl: linkData?.url,
                linkTarget: linkData?.target,
                textColor: toHex(item.textColor),
                backgroundColor: toHex(item.backgroundColor),
                backgroundOpacity: normalizeOpacity(
                    item.backgroundOpacity
                ),
                buttonFontSize: normalizeFontSize(
                    item.buttonFontSize
                ),
                buttonHeadingLevel: item.buttonHeadingLevel,
                row,
                col,
            };
        }
    );

    return (
        <ImageOverlay
            imageUrl={image.url}
            altText={image.properties?.altText}
            height={normalizeHeight(height)}
            overlayColor={toHex(overlayColor)}
            overlayOpacity={overlayOpacity}
            items={items}
        />
    );
};
